import {get, post, remove} from 'utils/axios/index'
import {toast} from 'react-toastify'
import {DefaultApiResponse} from 'models/ApiResponse'
import {AxiosError} from 'axios'
import {handleRejectedErrorMsg} from 'utils/helpers/handleError'
import {IApproveReject} from 'models/UserManagement'
import {IFormField} from 'models/Common'

const fetchUserList = async (
 status?: string,
 page?: number,
 pageSize?: number,
 params?: Record<string, unknown>,
 signal?: AbortSignal
) => {
 try {
  const query = {
   ...(status && {status}),
   ...(page && {page}),
   ...(pageSize && {page_size: pageSize}),
   ...(params ?? {}),
  }
  return await get<DefaultApiResponse>(
   `/users/v1/list`,
   query,
   undefined,
   undefined,
   signal
  ).then((res) => {
   if (res) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  if (e.message === 'canceled') return null
  handleRejectedErrorMsg(e)
  return null
 }
}

const bulkApproveRejectUsers = async (
 payload: IApproveReject
) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(`/users/v1/bulk-status-update/`, {
   ...payload,
  }).then((res) => {
   if (res.success) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const sendVerificationLink = async (
 payload: IFormField
) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(
   `/users/v1/send-verification-link`,
   {
    ...payload,
   }
  ).then((res) => {
   if (res.success) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const activateUser = async (user_id: number) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(`/users/v1/activate`, {
   user_id,
  }).then((res) => {
   if (res.success) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const deActivateUser = async (user_id: number) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(`/users/v1/deactivate`, {
   user_id,
  }).then((res) => {
   if (res.success) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const userActivationConfig = async (user_id: string) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/rb-platform/v1/user-activation-config`,
   {
    user_id,
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchAssignedSubDistrictList = async (
 district_id?: number,
 user_category_key?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(`/rb/v1/sub-districts/assigned`, {
   ...(district_id && {district_id}),
   ...(user_category_key && {user_category_key}),
  }).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchAssignedDistrictList = async (
 state_id?: number,
 user_category_key?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(`/rb/v1/districts/assigned`, {
   ...(state_id && {state_id}),
   ...(user_category_key && {user_category_key}),
  }).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchAssignedStateList = async (
 user_category_key?: string
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(`/rb/v1/states/assigned`, {
   ...(user_category_key && {user_category_key}),
  }).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchOrgMappingList = async (payload: IFormField) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/rb/v1/user/organisations`,
   payload
  ).then((res) => {
   if (res.results) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchUserOrgMappingList = async (
 payload: IFormField
) => {
 try {
  return await get<DefaultApiResponse>(
   `/rb/v1/user/organisation-mapping-list`,
   payload
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const removeUserOrgMappingList = async (
 payload: IFormField
) => {
 try {
  return await remove<DefaultApiResponse>(
   `/rb/v1/organisation/mappings`,
   payload
  ).then((res) => {
   if (res.success) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const removeUserOrgMappingManageUser = async (
 payload: IFormField
) => {
 try {
  return await remove<DefaultApiResponse>(
   `/rb/v1/user/organisation-mapping`,
   payload
  ).then((res) => {
   if (res.success) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

export const fetchOrganisationTypes = async (params?: {
 distributor_needed?: boolean
 user_category_key?: string
}) => {
 try {
  const response = await get<DefaultApiResponse>(
   '/rb/v1/organisation/types',
   params
  )

  if (response?.data) {
   return response.data
  }

  toast.error('Something went wrong')
  return []
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return []
 }
}

const fetchUserReportingOptions = async (
 user_id: number
) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/users/v1/user-reporting-options/`,
   {
    user_id,
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchUserReporting = async (user_id: number) => {
 try {
  return await get<DefaultApiResponse>(
   `/users/v1/user-reporting/retrieve`,
   {
    user_id,
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const submitUserReporting = async (payload: IFormField) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(
   `/users/v1/user-reporting/`,
   payload
  ).then((res) => {
   if (res.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchLOB = async (user_id: number) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/rb-platform/v1/onboarding/lob/retrieve`,
   {
    user_id,
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchLOBDetails = async (user_id: number) => {
 try {
  return await (
   await get
  )<DefaultApiResponse>(
   `/rb-platform/v1/business-verticals`,
   {
    user_id,
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const updateLOB = async (
 user_id: number,
 line_of_business: string
) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(`/rb-platform/v1/onboarding/lob`, {
   user_id,
   line_of_business,
  }).then((res) => {
   if (res.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const fetchGeoMapping = async (payload: IFormField) => {
 try {
  return await get<IFormField>(
   `/rb/v1/geo-mapping-list`,
   payload
  ).then((res) => {
   if (res) {
    return res
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const createGeoMapping = async (payload: IFormField) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(
   `/rb/v1/geo-mapping-create`,
   payload
  ).then((res) => {
   if (res.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const setActivationStepComplete = async (
 payload: IFormField
) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(
   `/rb-platform/v1/activation-steps/complete`,
   payload
  ).then((res) => {
   if (res.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

const createOrgMapping = async (payload: IFormField) => {
 try {
  return await (
   await post
  )<DefaultApiResponse>(
   `/rb/v1/user/organisation-mapping`,
   payload
  ).then((res) => {
   if (res.success) {
    return res.message
   } else {
    toast.error('Something went wrong')
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return null
 }
}

export {
 fetchUserList,
 bulkApproveRejectUsers,
 activateUser,
 deActivateUser,
 userActivationConfig,
 fetchAssignedSubDistrictList,
 fetchOrgMappingList,
 fetchUserReportingOptions,
 submitUserReporting,
 fetchLOB,
 updateLOB,
 createGeoMapping,
 setActivationStepComplete,
 createOrgMapping,
 fetchAssignedDistrictList,
 fetchGeoMapping,
 fetchUserOrgMappingList,
 removeUserOrgMappingList,
 fetchUserReporting,
 sendVerificationLink,
 removeUserOrgMappingManageUser,
 fetchLOBDetails,
 fetchAssignedStateList,
}
